import { FormEvent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFile } from '../../services/fileUploadService';
import {
  FeedbackSubject,
  Feeling,
  HelpRequestCategory,
  MOBILE_SUPPORT_SOURCE_APP,
  sendFeedback,
  sendHelpRequest,
} from '../../services/supportService';
import '../../styles/AccountTools.css';

const helpCategories: HelpRequestCategory[] = [
  'General Inquiry',
  'Technical Support',
  'Billing Issue',
  'Feedback',
  'Account Help',
];

const feedbackSubjects: FeedbackSubject[] = [
  'GENERAL_FEEDBACK',
  'PERFORMANCE',
  'APP_BUG',
  'FEATURE_REQUEST',
  'UI_FEEDBACK',
  'PAYMENTS',
  'TASKS',
  'SAFETY',
  'HARASSMENT',
  'ACCOUNT',
  'VERIFICATION',
  'SUPPORT',
  'OTHER',
];

const feelingOptions: Array<{ key: Feeling; label: string }> = [
  { key: 'LIKE', label: 'Like' },
  { key: 'DISLIKE', label: 'Dislike' },
  { key: 'HAPPY', label: 'Happy' },
  { key: 'SAD', label: 'Sad' },
];

const HelpSupport = () => {
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const actorId = user?.id || '';
  const [activeTab, setActiveTab] = useState<'help' | 'feedback'>(
    location.pathname.endsWith('/feedback') ? 'feedback' : 'help'
  );
  const [category, setCategory] = useState<HelpRequestCategory>('General Inquiry');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState<FeedbackSubject>('GENERAL_FEEDBACK');
  const [feeling, setFeeling] = useState<Feeling>('LIKE');
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setActiveTab(location.pathname.endsWith('/feedback') ? 'feedback' : 'help');
    setNotice(null);
  }, [location.pathname]);

  const ensureReadyToSubmit = () => {
    if (authLoading) {
      setNotice({ type: 'error', text: 'Your account is still loading. Please try again in a moment.' });
      return false;
    }

    if (!actorId) {
      setNotice({ type: 'error', text: 'Please sign in before submitting feedback or support requests.' });
      return false;
    }

    return true;
  };

  const handleHelpSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(null);
    if (!ensureReadyToSubmit()) return;

    if (!message.trim()) {
      setNotice({ type: 'error', text: 'Please type your message before submitting.' });
      return;
    }

    setSubmitting(true);
    try {
      await sendHelpRequest({
        category,
        message: message.trim(),
        sourceApp: MOBILE_SUPPORT_SOURCE_APP,
      }, actorId);
      setMessage('');
      setCategory('General Inquiry');
      setNotice({ type: 'success', text: 'Message sent. Thank you for contacting us.' });
    } catch (error) {
      setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Failed to send help request.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFeedbackSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(null);
    if (!ensureReadyToSubmit()) return;

    if (!content.trim()) {
      setNotice({ type: 'error', text: 'Please write feedback before submitting.' });
      return;
    }

    setSubmitting(true);
    try {
      const attachmentIds: string[] = [];
      const failedUploads: string[] = [];
      for (const file of attachments) {
        try {
          const result = await uploadFile(actorId, file, {
            purpose: 'USER_PROFILE',
            fileType: 'PHOTO',
            confirm: false,
          });
          attachmentIds.push(result.fileId);
        } catch {
          failedUploads.push(file.name);
        }
      }

      await sendFeedback({
        subject,
        subjectId: '',
        feeling,
        content: content.trim(),
        ...(attachmentIds.length ? { attachments: attachmentIds } : {}),
      }, actorId);

      setContent('');
      setAttachments([]);
      setNotice({
        type: 'success',
        text: failedUploads.length
          ? `Feedback sent. ${failedUploads.length} attachment${failedUploads.length === 1 ? '' : 's'} did not upload.`
          : 'Feedback sent. Thank you.',
      });
    } catch (error) {
      setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Failed to send feedback.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="tool-page">
      <div className="tool-header">
        <div>
          <h1>Help and Support</h1>
          <p>Contact support or send app feedback using the same mobile backend requests.</p>
        </div>
        <a className="tool-btn-secondary" href="https://olitask.com" target="_blank" rel="noreferrer">
          Learn More
        </a>
      </div>

      {notice && (
        <div className={`tool-message ${notice.type}`} role="status" aria-live="polite">
          {notice.text}
        </div>
      )}

      <div className="tool-tabs">
        <button className={activeTab === 'help' ? 'active' : ''} type="button" onClick={() => setActiveTab('help')}>
          Contact Us
        </button>
        <button className={activeTab === 'feedback' ? 'active' : ''} type="button" onClick={() => setActiveTab('feedback')}>
          Feedback
        </button>
      </div>

      {activeTab === 'help' ? (
        <form className="tool-card tool-form" onSubmit={handleHelpSubmit} aria-busy={submitting}>
          <h2>Contact Us</h2>
          <div className="tool-field">
            <label>Inquiry Category</label>
            <select value={category} onChange={(event) => setCategory(event.target.value as HelpRequestCategory)}>
              {helpCategories.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          <div className="tool-field">
            <label>Message</label>
            <textarea
              maxLength={500}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Tell us what happened or what you need help with."
            />
            <div className="tool-row-sub">{message.length}/500</div>
          </div>
          <button className="tool-btn" type="submit" disabled={submitting}>
            {submitting ? 'Sending...' : 'Submit'}
          </button>
        </form>
      ) : (
        <form className="tool-card tool-form" onSubmit={handleFeedbackSubmit} aria-busy={submitting}>
          <h2>Send Feedback</h2>
          <div className="tool-field">
            <label>Subject</label>
            <div className="tool-chips">
              {feedbackSubjects.map((item) => (
                <button
                  className={`tool-chip ${subject === item ? 'active' : ''}`}
                  key={item}
                  type="button"
                  onClick={() => setSubject(item)}
                >
                  {item.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>
          <div className="tool-field">
            <label>How do you feel?</label>
            <div className="tool-chips">
              {feelingOptions.map((item) => (
                <button
                  className={`tool-chip ${feeling === item.key ? 'active' : ''}`}
                  key={item.key}
                  type="button"
                  onClick={() => setFeeling(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="tool-field">
            <label>Attachments</label>
            <input type="file" accept="image/*" multiple onChange={(event) => setAttachments(Array.from(event.target.files || []))} />
            {attachments.length > 0 && (
              <div className="tool-upload-review">
                {attachments.map((file, index) => (
                  <div className="tool-upload-card" key={`${file.name}-${file.lastModified}-${index}`}>
                    <div className="tool-upload-preview">Photo</div>
                    <div className="tool-upload-meta">
                      <strong>{file.name}</strong>
                      <small>{Math.ceil(file.size / 1024)} KB</small>
                    </div>
                    <div className="tool-upload-actions single">
                      <button
                        type="button"
                        onClick={() => setAttachments((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="tool-field">
            <label>Your Feedback</label>
            <textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder="Write your feedback here." />
          </div>
          <button className="tool-btn" type="submit" disabled={submitting || authLoading || !content.trim()}>
            {submitting ? 'Sending...' : 'Submit Feedback'}
          </button>
        </form>
      )}
    </main>
  );
};

export default HelpSupport;
